import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Lottie from "lottie-react";
import { PrimaryButton } from "@acko-tech/building-blocks.ui.button";
import Modal from "@components/modal";
import { resolvePath } from "@utils/resolvePath";
import animation from "@public/lottie/new-car-bonus-offer.json";
import { Art, Banner, Panel } from "./styles";
import type { BannerProps, OfferProps } from "./types";
export function NewCarBonusBanner({
  title,
  description,
  is_visible = true
}: BannerProps) {
  if (!is_visible) return null;
  return (
    <Banner>
      <img
        src={resolvePath("/images/new-car-plan-positioning/bonus-shield.svg")}
        alt=""
      />
      <h2>{title}</h2>
      <p>{description}</p>
    </Banner>
  );
}

class AnimationBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}
const dismissedInMemory = new Set<string>();
export function NewCarBonusOffer({
  proposalId,
  heading,
  description,
  ctaText,
  actions,
  is_visible = true
}: OfferProps) {
  const ready = useSelector(
    (state: {
      configuration?: {
        normalisedConfigSuccess?: boolean;
        isSuccess?: boolean;
      };
    }) =>
      state.configuration?.normalisedConfigSuccess &&
      state.configuration?.isSuccess
  );
  const [animationFailed, setAnimationFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const started = useRef<string | null | undefined>(undefined);
  const continued = useRef(false);
  const panel = useRef<HTMLDivElement>(null);
  const onComplete = useRef(actions?.on_click);
  onComplete.current = actions?.on_click;
  const key =
    typeof proposalId === "string" && proposalId && !proposalId.startsWith("#")
      ? `new-car-bonus-offer:${proposalId}`
      : null;

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!ready || started.current === key) return;
    started.current = key;
    continued.current = false;
    setFinished(false);
    let dismissed = key ? dismissedInMemory.has(key) : false;
    try {
      dismissed = dismissed || Boolean(key && sessionStorage.getItem(key));
    } catch {
      /* Session storage may be unavailable in a webview. */
    }
    if (dismissed || !key || !is_visible) {
      setOpen(false);
      setFinished(true);
    } else setOpen(true);
  }, [ready, key, is_visible]);

  useEffect(() => {
    if (!finished || continued.current) return;
    // Let the existing modal finish closing before opening the next eligible popup.
    const timer = window.setTimeout(() => {
      continued.current = true;
      onComplete.current?.forEach((action) =>
        action.funCall?.(action.args || {})
      );
    }, 350);
    return () => window.clearTimeout(timer);
  }, [finished, key]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(
      () => panel.current?.querySelector("button")?.focus(),
      0
    );
    return () => window.clearTimeout(timer);
  }, [open]);

  const dismiss = () => {
    if (key) {
      dismissedInMemory.add(key);
      try {
        sessionStorage.setItem(key, "true");
      } catch {
        /* The in-memory fallback still prevents repeated prompts. */
      }
    }
    setOpen(false);
    setFinished(true);
  };

  return (
    <Modal
      showModal={open}
      actions={{}}
      hideCloseButton
      disableOverlayClose
      mobileOverlayBG="rgba(4, 2, 34, 0.8)"
      wrapperStyles={{ padding: "0px", borderRadius: "24px 24px 0 0" }}
      desktopWrapperStyles={{ borderRadius: "24px" }}
      bodyStyles={{ margin: 0 }}
    >
      <Panel
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-car-bonus-heading"
        onKeyDown={(event) => {
          if (event.key === "Tab") {
            event.preventDefault();
            panel.current?.querySelector("button")?.focus();
          }
        }}
      >
        <Art aria-hidden="true">
          <AnimationBoundary
            fallback={
              <img
                src={resolvePath(
                  "/images/new-car-plan-positioning/offer-gift.png"
                )}
                width={100}
                height={100}
                alt=""
              />
            }
          >
            {reducedMotion || animationFailed ? (
              <img
                src={resolvePath(
                  "/images/new-car-plan-positioning/offer-gift.png"
                )}
                width={100}
                height={100}
                alt=""
              />
            ) : (
              <Lottie
                animationData={animation}
                loop={false}
                autoplay
                onDataFailed={() => setAnimationFailed(true)}
              />
            )}
          </AnimationBoundary>
        </Art>
        <h2 id="new-car-bonus-heading">{heading}</h2>
        <p>{description}</p>
        <PrimaryButton isFullWidth onClick={dismiss}>
          {ctaText}
        </PrimaryButton>
      </Panel>
    </Modal>
  );
}
